// src/app/components/ErrorBoundary.tsx
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level rendering safety net. Catches otherwise-uncaught render
 * errors and shows a reload fallback instead of a blank page.
 *
 * Deliberately dumb, same as representative-admin's ErrorBoundary — no
 * retry-in-place. Unlike that one, this does not call into a monitoring
 * module: representative-admin's errorReporter is that app's own
 * integration and isn't part of this handoff, so wiring crash reporting
 * for super-admin is a separate, follow-up task rather than something
 * guessed at here.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[super-admin] uncaught render error', error, errorInfo.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
            direction: 'rtl',
          }}
        >
          <p style={{ fontSize: '1.125rem', fontWeight: 600 }}>خطایی رخ داد</p>
          <p style={{ opacity: 0.8 }}>لطفا دوباره تلاش کنید</p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '0.5rem 1rem',
              background: 'var(--vania-accent, #5b8cff)',
              color: '#fff',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            بارگذاری مجدد
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
